import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import { Box, Button, LinearProgress } from '@mui/material';
import React, { useState } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';
import { SALES_ENDPOINT } from 'utils/Constant';

import { ResumeReview } from './ResumeReviewV2';

interface EmailCampaignProps {
  jobId?: string;
  jobInfo?: any;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  try {
    const activeDate = new Date(dateStr);
    if (isNaN(activeDate.getTime())) return '';

    const currentDate = new Date();
    const diffTime = currentDate.getTime() - activeDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return `${diffDays} days ago`;
    }
  } catch (e) {
    return '';
  }
};

const toTitleCase = (str: string) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const EmailCampaign: React.FC<EmailCampaignProps> = ({ jobId, jobInfo }) => {
  const [selectedCandidate, setSelectedCandidate] = useState<{
    resumeId: string;
    fileId: string;
  } | null>(null);

  const endpoint = `${String(SALES_ENDPOINT.EmailCampaign)}/${String(jobId)}`;

  const { data, isLoading, refetch } = useFetchList<any>(
    ['email-campaign-results', jobId],
    endpoint,
    {
      enabled: !!jobId,
    },
  );

  const results: any[] = React.useMemo(() => {
    if (!data) return [];
    return Array.isArray(data?.data) ? data.data : data?.data?.data || [];
  }, [data]);

  if (isLoading) {
    return (
      <Box className="subcontractor-outreach-empty">
        <Box className="subcontractor-outreach-icon-wrap">
          <ForwardToInboxOutlinedIcon className="subcontractor-outreach-icon" />
        </Box>
        <h3 className="subcontractor-outreach-title">
          Loading Email Campaign Candidates...
        </h3>
        <p className="subcontractor-outreach-desc">
          Fetching candidates submitted via email campaign for this job.
        </p>
        <Box className="subcontractor-outreach-loader">
          <LinearProgress className="subcontractor-loader-bar" />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box className="job-board-results-container">
        <Box className="job-board-header">
          <h3 className="results-count">
            Email Campaign Results ({results.length})
          </h3>
        </Box>
        <Box className="results-list">
          {results.map((candidate: any) => {
            const fullName =
              toTitleCase(
                [candidate.firstName, candidate.lastName]
                  .filter(Boolean)
                  .join(' '),
              ) || '—';
            const statusLabel =
              candidate.status === 'readyForReview'
                ? 'Ready for Review'
                : candidate.status ?? 'Pending';

            const locationParts = [
              candidate.city,
              candidate.state,
              candidate.country,
            ].filter(Boolean);
            const location = locationParts.join(', ');

            const lastActiveRaw =
              candidate.dateLastActive || candidate.ceipalModifiedAt;
            const lastActive = formatDate(lastActiveRaw);
            return (
              <Box
                key={candidate.id ?? candidate.fileId}
                className="jb-result-card"
                onClick={() =>
                  setSelectedCandidate({
                    fileId: candidate.fileId,
                    resumeId: candidate.id,
                  })
                }
                sx={{ cursor: 'pointer' }}>
                <Box className="jb-card-left">
                  <Box className="jb-tag-row">
                    {candidate?.source?.name && (
                      <Box className="source-chip source-chip-link">
                        {candidate.source.name}
                      </Box>
                    )}
                    {(candidate.visaStatus ?? candidate.visa) && (
                      <Box className="jb-visa-tag">
                        {candidate.visaStatus ?? candidate.visa}
                      </Box>
                    )}
                    {location && (
                      <Box className="jb-location-tag">{location}</Box>
                    )}
                    {lastActive && (
                      <Box className="jb-last-active-tag">
                        Active: {lastActive}
                      </Box>
                    )}
                  </Box>
                  <p className="jb-candidate-name">{fullName}</p>
                  <p className="jb-candidate-email">{candidate.email}</p>
                </Box>
                <Box className="jb-card-right">
                  <Button
                    className="jb-pending-btn"
                    variant="outlined"
                    disabled
                    onClick={(e) => e.stopPropagation()}>
                    {statusLabel}
                  </Button>
                </Box>
              </Box>
            );
          })}
          {results.length === 0 && !isLoading && (
            <Box className="no-results">
              <p>No email campaign candidates found for this job.</p>
            </Box>
          )}
        </Box>
      </Box>

      {selectedCandidate && (
        <ResumeReview
          visible={!!selectedCandidate}
          onClose={() => {
            setSelectedCandidate(null);
            void refetch();
          }}
          resumeId={selectedCandidate.resumeId}
          fileId={selectedCandidate.fileId}
          salesReviewEnable={true}
        />
      )}
    </>
  );
};

export default EmailCampaign;
